import type { PollOptions, PollRecord } from "../types";
import { FlowAbortedError, PollTimeoutError } from "./errors";

export const DEFAULT_POLL_INTERVALS_MS = [2000, 3000, 5000];

export const intervalForAttempt = (
  attemptIndex: number,
  intervals: number[] = DEFAULT_POLL_INTERVALS_MS
) => intervals[Math.min(attemptIndex, intervals.length - 1)];

/** Total worst-case wait for a poll of `maxAttempts` (excludes request latency). */
export const maxPollDurationMs = (
  maxAttempts: number,
  intervals: number[] = DEFAULT_POLL_INTERVALS_MS
) => {
  let total = 0;
  for (let i = 0; i < maxAttempts - 1; i++) total += intervalForAttempt(i, intervals);
  return total;
};

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) return reject(new FlowAbortedError());
    const t = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(t);
      reject(new FlowAbortedError());
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });

export interface PollUntilArgs<T> extends PollOptions<T> {
  signal?: AbortSignal;
  onUpdate: (record: PollRecord) => void;
}

/**
 * Polls `fetch` until `predicate` passes. Waits 2s, then 3s, then 5s (and 5s
 * thereafter) between attempts. Emits a PollRecord after each observation and
 * before each wait so the UI can render "next check in Xs" countdowns.
 */
export const pollUntil = async <T>(args: PollUntilArgs<T>): Promise<T> => {
  const intervals = args.intervalsMs ?? DEFAULT_POLL_INTERVALS_MS;
  const maxAttempts = Math.max(1, args.maxAttempts ?? 8);
  const record: PollRecord = {
    trigger: args.trigger,
    expected: args.expected,
    startedAt: Date.now(),
    attempts: 0,
    maxAttempts,
  };
  args.onUpdate({ ...record });

  let lastValue: T | undefined;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (args.signal?.aborted) {
      record.outcome = "aborted";
      record.endedAt = Date.now();
      args.onUpdate({ ...record });
      throw new FlowAbortedError();
    }
    record.attempts = attempt + 1;
    record.nextCheckAt = undefined;
    record.currentWaitMs = undefined;
    args.onUpdate({ ...record });

    lastValue = await args.fetch();
    record.lastObserved = args.describe(lastValue);

    if (args.predicate(lastValue)) {
      record.outcome = "success";
      record.endedAt = Date.now();
      args.onUpdate({ ...record });
      return lastValue;
    }

    if (attempt < maxAttempts - 1) {
      const wait = intervalForAttempt(attempt, intervals);
      record.currentWaitMs = wait;
      record.nextCheckAt = Date.now() + wait;
      args.onUpdate({ ...record });
      await sleep(wait, args.signal);
    }
  }

  record.outcome = "timeout";
  record.endedAt = Date.now();
  args.onUpdate({ ...record });
  throw new PollTimeoutError(
    `Timed out waiting for: ${args.expected} (after ${maxAttempts} checks, ${Math.round(
      (record.endedAt - record.startedAt) / 1000
    )}s)`,
    maxAttempts,
    args.expected,
    record.lastObserved || "no observation"
  );
};
